import { useAuth } from "../auth/AuthContext";
import { listBranches } from "../api/branchesApi";
import { useAsyncData } from "../lib/useAsyncData";

/**
 * Shared `GET /branches`-backed hook, used everywhere a form/filter needs to resolve a
 * real branch id — replacing every remaining raw-UUID branch input across this app
 * (business-owner walkthrough item 3, most visibly `InventoryListPage.tsx`'s old
 * "paste a branch UUID" hint text).
 */
export function useBranchOptions() {
  const { ensureFreshToken } = useAuth();
  return useAsyncData(() => ensureFreshToken().then((token) => listBranches(token)), []);
}

export interface BranchSelectProps {
  id: string;
  label: string;
  value: string;
  onChange: (branchId: string) => void;
  required?: boolean;
  /** When set, renders a leading "all branches"-style option with this label and an
   * empty value — used by filters (e.g. the Inventory list's branch filter) rather than
   * create forms, which should force a real selection. */
  allOptionLabel?: string;
  disabled?: boolean;
}

export function BranchSelect({ id, label, value, onChange, required, allOptionLabel, disabled }: BranchSelectProps) {
  const branchesState = useBranchOptions();
  const branches = branchesState.data ?? [];

  return (
    <div className="sl-field">
      <label className="sl-field__label" htmlFor={id}>
        {label}
      </label>
      <select
        id={id}
        className="sl-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        disabled={disabled || branchesState.loading}
      >
        {allOptionLabel ? <option value="">{allOptionLabel}</option> : <option value="">Select a branch…</option>}
        {branches.map((branch) => (
          <option key={branch.id} value={branch.id}>
            {branch.name}
            {!branch.is_active ? " (inactive)" : ""}
          </option>
        ))}
      </select>
      {branchesState.error ? <p className="sl-field__error">{branchesState.error}</p> : null}
    </div>
  );
}
